// deployment_class: customer_env_allowed
import { useLayoutEffect, useRef, useState } from 'react';
import { Dashboard } from '../dashboard/Dashboard';
import { DashboardContentProvider } from '../dashboard/dashboard-source';
import { useExecutiveContent } from '../dashboard/useExecutiveContent';

/**
 * DashboardHome — the authenticated landing surface of the app.
 *
 * Per the CEO-First Dashboard stack, the logged-in home IS the dashboard. As of
 * the 2026-06-28 redesign this renders the REAL React CEO dashboard
 * ({@link Dashboard}) — no longer an iframe to a served HTML demo. The demo IS
 * the app; there is no separate mockup (E3 lock 2026-06-28).
 *
 * The persistent TopBar ([role="banner"]) renders ABOVE this; the dashboard
 * fills the space BELOW it. We size the container to `window.innerHeight −
 * TopBar height`, measured at runtime — no magic pixel constant, robust to the
 * bar's height and to resize. An unauthenticated visitor never reaches this
 * component — `RequireAuthGate` redirects to `/auth/login` first.
 */
export function DashboardHome(): JSX.Element {
  const [height, setHeight] = useState('100vh');
  // Desktop keeps the fixed-viewport model (overflow:hidden · the dashboard's
  // content column scrolls internally). On phones (≤768px) the dashboard stacks
  // to a single column that is TALLER than the viewport, so THIS wrapper becomes
  // the scroll container (overflow:auto) — otherwise the below-the-fold content
  // is clipped and unreachable. (Fable mobile pass · E3: never break mobile.)
  const [overflowY, setOverflowY] = useState<'hidden' | 'auto'>('hidden');
  // On mobile THIS wrapper is the scroll container; hand it to the dashboard so
  // its scroll-to-top-on-navigation discipline targets the real scroller.
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const measure = (): void => {
      const bar = document.querySelector('[role="banner"]');
      const barHeight = bar instanceof HTMLElement ? bar.offsetHeight : 0;
      setHeight(`${Math.max(0, window.innerHeight - barHeight)}px`);
      setOverflowY(window.innerWidth <= 768 ? 'auto' : 'hidden');
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // UI-B2 · B3 · B7 — the SUPPLIER side of the content seam.
  //
  // `Dashboard` has always READ the seam (`useDashboardContent()` at
  // Dashboard.tsx:467, merging supplied content over the bare module). Nothing
  // ever WROTE to it, so every figure on the cockpit came from hand-authored
  // constants — which is how it told the CEO there were 10 active partnerships
  // while the database held 4. The reader existed; the writer did not.
  //
  // `content` is `null` while loading and on failure, which is the truthful
  // value: we have no figures, as distinct from an agency that HAS none. The
  // dashboard then renders its bare shape rather than asserting emptiness it
  // has not earned.
  const { content } = useExecutiveContent();

  return (
    <div ref={scrollRef} style={{ height, overflow: overflowY, WebkitOverflowScrolling: 'touch' }}>
      <DashboardContentProvider content={content}>
        <Dashboard scrollContainerRef={scrollRef} />
      </DashboardContentProvider>
    </div>
  );
}
